'use client';

import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CookieIcon } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from '~/components/ui/card';
import { cn } from '~/lib/utils';
import { getCookieConsent, setCookieConsent } from '~/lib/cookie-utils';

interface CookieConsentProps {
  variant?: 'default' | 'small';
  demo?: boolean;
  onAcceptCallback?: () => void;
  onDeclineCallback?: () => void;
}

export default function CookieConsent({
  variant = 'small',
  demo = false,
  onAcceptCallback = () => {},
  onDeclineCallback = () => {},
}: CookieConsentProps) {
  const { t } = useTranslation();
  type VisibilityState = 'visible' | 'hiding' | 'hidden';
  const [visibility, setVisibility] = React.useState<VisibilityState>('hidden');
  const HIDE_DELAY = 700; // Define delay constant

  // Helper function to update consent state via gtag
  const updateConsent = (granted: boolean) => {
    const consentState = granted ? 'granted' : 'denied';
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: consentState,
        ad_storage: consentState,
        ad_user_data: consentState,
        ad_personalization: consentState,
      });
    }
  };

  const accept = () => {
    setVisibility('hiding');
    setCookieConsent(true);
    updateConsent(true); // Update consent state to granted
    setTimeout(() => {
      setVisibility('hidden');
    }, HIDE_DELAY);
    onAcceptCallback();
  };

  const decline = () => {
    setVisibility('hiding');
    setCookieConsent(false); // Explicitly set consent to false in storage
    updateConsent(false); // Update consent state to denied
    setTimeout(() => {
      setVisibility('hidden');
    }, HIDE_DELAY);
    onDeclineCallback();
  };

  React.useEffect(() => {
    const consentGiven = getCookieConsent();

    if (consentGiven === null && !demo) {
      // No consent set yet, show the banner
      setVisibility('visible');
      // Default is already 'denied' via root.tsx, no need to update here
    } else if (consentGiven === true && !demo) {
      // Consent previously granted, hide banner and ensure gtag state is updated
      setVisibility('hidden');
      updateConsent(true);
    } else if (consentGiven === false && !demo) {
      // Consent previously denied, show banner (or keep hidden if desired)
      // For this example, we'll show it again if denied previously
      setVisibility('visible');
      // Default is already 'denied', but we can re-affirm
      updateConsent(false);
    } else if (demo) {
      // Demo mode, always show
      setVisibility('visible');
    } else {
      // Should not happen, but hide by default
      setVisibility('hidden');
    }
  }, [demo]);

  const commonClasses = cn(
    'fixed z-[200] bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 w-full sm:max-w-md duration-700 transition-[opacity,transform]',
    {
      'translate-y-0 opacity-100': visibility === 'visible',
      'translate-y-8 opacity-0': visibility === 'hiding',
      hidden: visibility === 'hidden', // Use hidden class directly when state is 'hidden'
    }
  );

  // Return null immediately if hidden to avoid rendering anything
  if (visibility === 'hidden') {
    return null;
  }

  // Refactored structure to reduce duplication
  return (
    <div className={commonClasses}>
      <Card className="m-3">
        <CardHeader
          className={cn(
            'flex flex-row items-center justify-between',
            variant === 'default' ? 'border-b pb-4' : 'pb-2' // Conditional padding and border
          )}
        >
          <CardTitle className="text-lg">{t('cookieConsent.title')}</CardTitle>
          <CookieIcon className="h-[1.2rem] w-[1.2rem]" />
        </CardHeader>
        <CardContent
          className={cn(
            variant === 'default' ? 'pt-4' : 'pt-0 pb-3' // Conditional padding
          )}
        >
          <p
            className={cn(
              'text-left text-sm', // Common style
              variant === 'default'
                ? 'font-normal' // Default variant style
                : 'text-muted-foreground' // Small variant style
            )}
          >
            {t('cookieConsent.description')}
            {variant === 'default' && ( // Only show extra details in default variant
              <>
                <br />
                <br />
                <span className="text-xs">
                  <Trans i18nKey="cookieConsent.agreement">
                    By clicking "
                    <span className="font-medium opacity-80">Accept</span>
                    ", you agree to our use of cookies.
                  </Trans>
                </span>
                <br />
                <a href="/privacy" className="text-xs underline">
                  {' '}
                  {t('cookieConsent.learnMore')}
                </a>
              </>
            )}
          </p>
        </CardContent>
        <CardFooter
          className={cn(
            'flex gap-2 border-t',
            variant === 'default'
              ? 'dark:bg-background/20 pt-4' // Default variant padding and background
              : 'items-center pt-3' // Small variant padding and alignment
          )}
        >
          <Button
            onClick={accept}
            size={variant === 'small' ? 'sm' : undefined} // Conditional size
            className={cn(variant === 'small' && 'rounded-full')} // Conditional shape
          >
            {t('cookieConsent.accept')}
          </Button>
          <Button
            onClick={decline}
            variant={variant === 'default' ? 'secondary' : 'outline'} // Conditional variant
            size={variant === 'small' ? 'sm' : undefined} // Conditional size
            className={cn(variant === 'small' && 'rounded-full')} // Conditional shape
          >
            {t('cookieConsent.decline')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
